// Eingebaute SRS-Plugins. Jedes Plugin hat eine Parameterbeschreibung (für Oberfläche, Auto-Mix und Speicherung)
// und eine Fabrik, die es in einem AudioContext oder OfflineAudioContext baut. So klingt der Export genau wie die Wiedergabe.
// Dynamik (Kompressor, Gate, De-Esser, Limiter) läuft als AudioWorklet (public/dsp-worklet.js), EQ, Sättigung,
// Hall und Delay mit den nativen Web-Audio-Knoten.
import type { InsertSlot, Track } from '@srs/shared';

export type Params = Record<string, number>;
export interface ParamDef {
  key: string; label: string; min: number; max: number; def: number;
  unit?: string; scale?: 'log' | 'lin'; step?: number;
  /** Auswahl (Wert = Index) */
  choices?: string[];
  /** Schalter 0/1 */
  bool?: boolean;
}
export interface PluginEnv { bpm: number; offline?: boolean }
export interface Meter { gr: number; inDb: number; outDb: number }
export interface PluginInstance {
  input: AudioNode; output: AudioNode;
  set(p: Params, env: PluginEnv): void;
  dispose(): void;
  /** Messwerte der Dynamik-Plugins (Gain-Reduction in dB, ≤ 0) */
  meter?: Meter;
  /** EQ: Spektrum hinter dem EQ und die Filter für die Kurve */
  analyser?: AnalyserNode;
  filters?: BiquadFilterNode[];
}
export interface PluginDef {
  id: string; name: string; short: string; role: string;
  group: 'EQ' | 'Dynamik' | 'Farbe' | 'Raum' | 'Werkzeug' | 'Bridge';
  params: ParamDef[];
  presets: { name: string; params: Params }[];
  create(ctx: BaseAudioContext, p: Params, env: PluginEnv): PluginInstance;
}

const dbToLin = (d: number) => Math.pow(10, d / 20);
const live = (ctx: BaseAudioContext): ctx is AudioContext => typeof AudioContext !== 'undefined' && ctx instanceof AudioContext;
/** Parameter weich setzen (live, gegen Knackser) oder direkt (Offline-Rendering) */
function setP(ctx: BaseAudioContext, a: AudioParam, v: number, tc = 0.01) {
  if (!Number.isFinite(v)) return;
  if (live(ctx) && ctx.state === 'running') a.setTargetAtTime(v, ctx.currentTime, tc);
  else a.value = v;
}
/** Stereo-Eingang: Mono wird verdoppelt, damit jedes Plugin immer zwei Kanäle bekommt */
function stereoGain(ctx: BaseAudioContext, gain = 1) {
  const g = ctx.createGain();
  g.channelCount = 2; g.channelCountMode = 'explicit'; g.channelInterpretation = 'speakers';
  g.gain.value = gain;
  return g;
}
const qDb = (q: number) => 20 * Math.log10(q); // Hoch-/Tiefpass erwarten Q in dB

// ---------------------------------------------------------------------------
// SRS EQ: Hoch- und Tiefpass (12 bis 48 dB/Okt., Butterworth), Low-Shelf, drei Glocken, High-Shelf, Ausgang
const SLOPES = ['12 dB', '24 dB', '36 dB', '48 dB'];
const BUTTER: number[][] = [[0.70711], [0.54120, 1.30656], [0.51764, 0.70711, 1.93185], [0.50980, 0.60134, 0.89998, 2.56292]];

const eqParams: ParamDef[] = [
  { key: 'hpOn', label: 'Low-Cut', min: 0, max: 1, def: 0, bool: true },
  { key: 'hpF', label: 'Low-Cut Frequenz', min: 20, max: 1000, def: 80, unit: 'Hz', scale: 'log' },
  { key: 'hpSlope', label: 'Low-Cut Steilheit', min: 0, max: 3, def: 1, choices: SLOPES },
  { key: 'lsOn', label: 'Low-Shelf', min: 0, max: 1, def: 1, bool: true },
  { key: 'lsF', label: 'Low-Shelf Frequenz', min: 20, max: 1000, def: 100, unit: 'Hz', scale: 'log' },
  { key: 'lsG', label: 'Low-Shelf Gain', min: -18, max: 18, def: 0, unit: 'dB', step: 0.1 },
  { key: 'b1On', label: 'Band 1', min: 0, max: 1, def: 1, bool: true },
  { key: 'b1F', label: 'Band 1 Frequenz', min: 20, max: 20000, def: 250, unit: 'Hz', scale: 'log' },
  { key: 'b1G', label: 'Band 1 Gain', min: -18, max: 18, def: 0, unit: 'dB', step: 0.1 },
  { key: 'b1Q', label: 'Band 1 Q', min: 0.1, max: 18, def: 1, scale: 'log', step: 0.01 },
  { key: 'b2On', label: 'Band 2', min: 0, max: 1, def: 1, bool: true },
  { key: 'b2F', label: 'Band 2 Frequenz', min: 20, max: 20000, def: 1000, unit: 'Hz', scale: 'log' },
  { key: 'b2G', label: 'Band 2 Gain', min: -18, max: 18, def: 0, unit: 'dB', step: 0.1 },
  { key: 'b2Q', label: 'Band 2 Q', min: 0.1, max: 18, def: 1, scale: 'log', step: 0.01 },
  { key: 'b3On', label: 'Band 3', min: 0, max: 1, def: 1, bool: true },
  { key: 'b3F', label: 'Band 3 Frequenz', min: 20, max: 20000, def: 4000, unit: 'Hz', scale: 'log' },
  { key: 'b3G', label: 'Band 3 Gain', min: -18, max: 18, def: 0, unit: 'dB', step: 0.1 },
  { key: 'b3Q', label: 'Band 3 Q', min: 0.1, max: 18, def: 1, scale: 'log', step: 0.01 },
  { key: 'hsOn', label: 'High-Shelf', min: 0, max: 1, def: 1, bool: true },
  { key: 'hsF', label: 'High-Shelf Frequenz', min: 1000, max: 20000, def: 10000, unit: 'Hz', scale: 'log' },
  { key: 'hsG', label: 'High-Shelf Gain', min: -18, max: 18, def: 0, unit: 'dB', step: 0.1 },
  { key: 'lpOn', label: 'High-Cut', min: 0, max: 1, def: 0, bool: true },
  { key: 'lpF', label: 'High-Cut Frequenz', min: 1000, max: 22000, def: 18000, unit: 'Hz', scale: 'log' },
  { key: 'lpSlope', label: 'High-Cut Steilheit', min: 0, max: 3, def: 1, choices: SLOPES },
  { key: 'out', label: 'Ausgang', min: -18, max: 18, def: 0, unit: 'dB', step: 0.1 },
];

function createEq(ctx: BaseAudioContext, p: Params): PluginInstance {
  const input = stereoGain(ctx), output = ctx.createGain();
  const mk = () => { const b = ctx.createBiquadFilter(); b.type = 'peaking'; b.gain.value = 0; return b; };
  const hp = [mk(), mk(), mk(), mk()], lp = [mk(), mk(), mk(), mk()];
  const ls = ctx.createBiquadFilter(); ls.type = 'lowshelf';
  const b1 = mk(), b2 = mk(), b3 = mk();
  const hs = ctx.createBiquadFilter(); hs.type = 'highshelf';
  const analyser = ctx.createAnalyser(); analyser.fftSize = 8192; analyser.smoothingTimeConstant = 0.8; analyser.minDecibels = -96; analyser.maxDecibels = 0;
  const chain = [...hp, ls, b1, b2, b3, hs, ...lp];
  let prev: AudioNode = input;
  for (const n of chain) { prev.connect(n); prev = n; }
  prev.connect(output); output.connect(analyser);
  const cut = (nodes: BiquadFilterNode[], on: boolean, f: number, slope: number, type: 'highpass' | 'lowpass') => {
    const qs = BUTTER[Math.max(0, Math.min(3, Math.round(slope)))];
    nodes.forEach((n, i) => {
      if (on && i < qs.length) { n.type = type; setP(ctx, n.frequency, f); n.Q.value = qDb(qs[i]); }
      else { n.type = 'peaking'; n.gain.value = 0; }
    });
  };
  const inst: PluginInstance = {
    input, output, analyser, filters: chain,
    set(q) {
      cut(hp, q.hpOn > 0.5, q.hpF, q.hpSlope, 'highpass');
      cut(lp, q.lpOn > 0.5, q.lpF, q.lpSlope, 'lowpass');
      setP(ctx, ls.frequency, q.lsF); setP(ctx, ls.gain, q.lsOn > 0.5 ? q.lsG : 0);
      for (const [n, k] of [[b1, 'b1'], [b2, 'b2'], [b3, 'b3']] as const) { setP(ctx, n.frequency, q[`${k}F`]); n.Q.value = q[`${k}Q`]; setP(ctx, n.gain, q[`${k}On`] > 0.5 ? q[`${k}G`] : 0); }
      setP(ctx, hs.frequency, q.hsF); setP(ctx, hs.gain, q.hsOn > 0.5 ? q.hsG : 0);
      setP(ctx, output.gain, dbToLin(q.out));
    },
    dispose() { [input, output, analyser, ...chain].forEach((n) => n.disconnect()); },
  };
  inst.set(p, { bpm: 120 });
  return inst;
}

// ---------------------------------------------------------------------------
// Dynamik im Worklet
function worklet(ctx: BaseAudioContext, name: string, p: Params): PluginInstance {
  try {
    const node = new AudioWorkletNode(ctx, name, { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [2], channelCount: 2, channelCountMode: 'explicit', channelInterpretation: 'speakers', processorOptions: { params: p } });
    const inst: PluginInstance = {
      input: node, output: node, meter: { gr: 0, inDb: -120, outDb: -120 },
      set(q) { node.port.postMessage(q); },
      dispose() { node.port.onmessage = null; node.disconnect(); },
    };
    node.port.onmessage = (e) => { inst.meter = e.data as Meter; };
    return inst;
  } catch {
    // Worklet nicht geladen (sehr alter Browser): Signal unverändert durchreichen, statt Ton zu verlieren
    const g = stereoGain(ctx);
    return { input: g, output: g, set() {}, dispose() { g.disconnect(); } };
  }
}

// ---------------------------------------------------------------------------
// Sättigung: Kennlinie mit 4-fachem Oversampling, Tilt-Klang, DC-Filter
const SAT_TYPES = ['Band', 'Röhre', 'Clip'];
function satCurve(type: number, driveDb: number): Float32Array {
  const g = dbToLin(driveDb), N = 8192, c = new Float32Array(N);
  // Kurve deckt ±4 ab (12 dB Reserve); vorher ×0,25, nachher ×4
  for (let i = 0; i < N; i++) {
    const x = ((i / (N - 1)) * 2 - 1) * 4;
    let y: number;
    if (type === 1) { const k = x >= 0 ? g : g * 1.35; y = Math.tanh(k * x) / k; }
    else if (type === 2) y = x / Math.pow(1 + Math.pow(Math.abs(g * x), 6), 1 / 6);
    else y = Math.tanh(g * x) / g;
    c[i] = y / 4;
  }
  return c;
}
function createSat(ctx: BaseAudioContext, p: Params): PluginInstance {
  const input = stereoGain(ctx, 0.25), shaper = ctx.createWaveShaper(), post = ctx.createGain(), dc = ctx.createBiquadFilter(), lo = ctx.createBiquadFilter(), hi = ctx.createBiquadFilter(), output = ctx.createGain();
  shaper.oversample = '4x'; post.gain.value = 4;
  dc.type = 'highpass'; dc.frequency.value = 10; dc.Q.value = qDb(0.7071);
  lo.type = 'lowshelf'; lo.frequency.value = 700; hi.type = 'highshelf'; hi.frequency.value = 700;
  input.connect(shaper).connect(post).connect(dc).connect(lo).connect(hi).connect(output);
  let key = '';
  const inst: PluginInstance = {
    input, output,
    set(q) {
      const k = `${q.type}|${q.drive}`;
      if (k !== key) { key = k; shaper.curve = satCurve(Math.round(q.type), q.drive) as Float32Array<ArrayBuffer>; }
      setP(ctx, lo.gain, -q.tone / 2); setP(ctx, hi.gain, q.tone / 2);
      setP(ctx, output.gain, dbToLin(q.out));
    },
    dispose() { [input, shaper, post, dc, lo, hi, output].forEach((n) => n.disconnect()); },
  };
  inst.set(p, { bpm: 120 });
  return inst;
}

// ---------------------------------------------------------------------------
// Hall: berechnete Impulsantwort (Plate, Raum, Halle, Kammer) mit frequenzabhängigem Abklingen
export const REVERB_TYPES = ['Plate', 'Raum', 'Halle', 'Kammer'];
const RT = [
  { build: 1, er: false, bright: 13000 },
  { build: 0.6, er: true, bright: 9500 },
  { build: 16, er: false, bright: 8000 },
  { build: 4, er: true, bright: 10000 },
];
function rng(seed: number) { return () => { seed |= 0; seed = (seed + 0x6d2b79f5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }
export function reverbIR(ctx: BaseAudioContext, type: number, decay: number, damp: number, width: number): AudioBuffer {
  const sr = ctx.sampleRate, T = RT[Math.max(0, Math.min(3, Math.round(type)))];
  const len = Math.min(sr * 10, Math.floor(sr * (decay * 1.15 + 0.05)));
  const b = ctx.createBuffer(2, len, sr);
  for (let ch = 0; ch < 2; ch++) {
    const d = b.getChannelData(ch), rnd = rng(1234 + ch * 7919);
    let y = 0, a = 0;
    for (let i = 0; i < len; i++) {
      const t = i / sr;
      if ((i & 31) === 0) { const fc = Math.max(400, T.bright * Math.exp((-damp * 3.2 * t) / Math.max(0.2, decay))); a = Math.exp((-2 * Math.PI * fc) / sr); }
      y = (1 - a) * (rnd() * 2 - 1) + a * y;
      d[i] = y * Math.exp((-6.907755 * t) / decay) * (1 - Math.exp(-t / (T.build / 1000)));
    }
    if (T.er) for (let k = 0; k < 14; k++) {
      const at = Math.floor((0.004 + rnd() * 0.06) * sr), g = (rnd() < 0.5 ? -1 : 1) * 0.7 * Math.exp(-at / sr * 25);
      for (let j = 0; j < 24 && at + j < len; j++) d[at + j] += g * (1 - j / 24);
    }
  }
  const w = Math.max(0, Math.min(1, width));
  const L = b.getChannelData(0), R = b.getChannelData(1);
  for (let i = 0; i < len; i++) { const m = (L[i] + R[i]) / 2, s = (L[i] - R[i]) / 2; L[i] = m + w * s; R[i] = m - w * s; }
  return b;
}
function createReverb(ctx: BaseAudioContext, p: Params, env: PluginEnv): PluginInstance {
  const input = stereoGain(ctx), dry = ctx.createGain(), lc = ctx.createBiquadFilter(), hc = ctx.createBiquadFilter(), pre = ctx.createDelay(0.5), conv = ctx.createConvolver(), wet = ctx.createGain(), output = ctx.createGain();
  lc.type = 'highpass'; lc.Q.value = qDb(0.7071); hc.type = 'lowpass'; hc.Q.value = qDb(0.7071);
  input.connect(dry).connect(output);
  input.connect(lc).connect(hc).connect(pre).connect(conv).connect(wet).connect(output);
  let key = '', timer: ReturnType<typeof setTimeout> | undefined;
  const inst: PluginInstance = {
    input, output,
    set(q, e) {
      const k = `${q.type}|${q.decay}|${q.damp}|${q.width}`;
      if (k !== key) {
        key = k;
        const build = () => { conv.buffer = reverbIR(ctx, q.type, q.decay, q.damp / 100, q.width / 100); };
        clearTimeout(timer);
        if (e.offline || !conv.buffer) build(); else timer = setTimeout(build, 120);
      }
      setP(ctx, lc.frequency, q.locut); setP(ctx, hc.frequency, q.hicut);
      setP(ctx, pre.delayTime, q.pre / 1000);
      const m = Math.max(0, Math.min(1, q.mix / 100));
      setP(ctx, dry.gain, Math.cos((m * Math.PI) / 2)); setP(ctx, wet.gain, Math.sin((m * Math.PI) / 2));
    },
    dispose() { clearTimeout(timer); [input, dry, lc, hc, pre, conv, wet, output].forEach((n) => n.disconnect()); },
  };
  inst.set(p, env);
  return inst;
}

// ---------------------------------------------------------------------------
// Delay: tempo-synchron, Feedback mit Filtern in der Schleife, Ping-Pong
export const DELAY_DIVS = ['1/4', '1/8', '1/8 punktiert', '1/8 Triole', '1/16', '1/2', 'frei (ms)'];
const DIV_BEATS = [1, 0.5, 0.75, 1 / 3, 0.25, 2, 0];
function createDelay(ctx: BaseAudioContext, p: Params, env: PluginEnv): PluginInstance {
  const input = stereoGain(ctx), output = ctx.createGain(), dry = ctx.createGain(), wet = ctx.createGain();
  const split = ctx.createChannelSplitter(2), merge = ctx.createChannelMerger(2);
  const dL = ctx.createDelay(4), dR = ctx.createDelay(4);
  const filt = () => { const h = ctx.createBiquadFilter(), l = ctx.createBiquadFilter(); h.type = 'highpass'; l.type = 'lowpass'; h.Q.value = qDb(0.7071); l.Q.value = qDb(0.7071); h.connect(l); return [h, l] as const; };
  const [hL, lL] = filt(), [hR, lR] = filt();
  const g = () => ctx.createGain();
  const nLL = g(), nRR = g(), nRL = g(), xLL = g(), xLR = g(), xRR = g(), xRL = g();
  input.connect(dry).connect(output);
  input.connect(split);
  split.connect(nLL, 0).connect(dL); split.connect(nRL, 1).connect(dL); split.connect(nRR, 1).connect(dR);
  dL.connect(hL); dR.connect(hR);
  lL.connect(xLL).connect(dL); lL.connect(xLR).connect(dR); lR.connect(xRR).connect(dR); lR.connect(xRL).connect(dL);
  lL.connect(merge, 0, 0); lR.connect(merge, 0, 1);
  merge.connect(wet).connect(output);
  const inst: PluginInstance = {
    input, output,
    set(q, e) {
      const div = Math.max(0, Math.min(DIV_BEATS.length - 1, Math.round(q.div)));
      const sec = DIV_BEATS[div] ? (DIV_BEATS[div] * 60) / Math.max(30, e.bpm) : q.ms / 1000;
      setP(ctx, dL.delayTime, Math.min(3.9, sec), 0.05); setP(ctx, dR.delayTime, Math.min(3.9, sec), 0.05);
      const fb = Math.max(0, Math.min(0.95, q.fb / 100)), pp = q.pp > 0.5;
      setP(ctx, nLL.gain, pp ? 0.5 : 1); setP(ctx, nRL.gain, pp ? 0.5 : 0); setP(ctx, nRR.gain, pp ? 0 : 1);
      setP(ctx, xLL.gain, pp ? 0 : fb); setP(ctx, xRR.gain, pp ? 0 : fb); setP(ctx, xLR.gain, pp ? fb : 0); setP(ctx, xRL.gain, pp ? fb : 0);
      for (const h of [hL, hR]) setP(ctx, h.frequency, q.hp);
      for (const l of [lL, lR]) setP(ctx, l.frequency, q.lp);
      const m = Math.max(0, Math.min(1, q.mix / 100));
      setP(ctx, dry.gain, Math.cos((m * Math.PI) / 2)); setP(ctx, wet.gain, Math.sin((m * Math.PI) / 2));
    },
    dispose() { [input, output, dry, wet, split, merge, dL, dR, hL, lL, hR, lR, nLL, nRR, nRL, xLL, xLR, xRR, xRL].forEach((n) => n.disconnect()); },
  };
  inst.set(p, env);
  return inst;
}

// ---------------------------------------------------------------------------
// Utility: Pegel, Stereobreite, Phase, Mono
function createUtility(ctx: BaseAudioContext, p: Params): PluginInstance {
  const input = stereoGain(ctx), split = ctx.createChannelSplitter(2), merge = ctx.createChannelMerger(2), output = ctx.createGain();
  const LL = ctx.createGain(), LR = ctx.createGain(), RL = ctx.createGain(), RR = ctx.createGain();
  input.connect(split);
  split.connect(LL, 0).connect(merge, 0, 0); split.connect(LR, 0).connect(merge, 0, 1);
  split.connect(RL, 1).connect(merge, 0, 0); split.connect(RR, 1).connect(merge, 0, 1);
  merge.connect(output);
  const inst: PluginInstance = {
    input, output,
    set(q) {
      const w = q.mono > 0.5 ? 0 : Math.max(0, Math.min(2, q.width / 100)), sL = q.phaseL > 0.5 ? -1 : 1, sR = q.phaseR > 0.5 ? -1 : 1;
      setP(ctx, LL.gain, ((1 + w) / 2) * sL); setP(ctx, LR.gain, ((1 - w) / 2) * sL);
      setP(ctx, RL.gain, ((1 - w) / 2) * sR); setP(ctx, RR.gain, ((1 + w) / 2) * sR);
      setP(ctx, output.gain, dbToLin(q.gain));
    },
    dispose() { [input, split, merge, output, LL, LR, RL, RR].forEach((n) => n.disconnect()); },
  };
  inst.set(p, { bpm: 120 });
  return inst;
}

// ---------------------------------------------------------------------------
export const CHARACTERS = ['Clean', 'Opto', 'Punch', 'Glue'];
export const PLUGINS: PluginDef[] = [
  {
    id: 'srs:eq', name: 'SRS EQ', short: 'EQ', role: 'eq', group: 'EQ', params: eqParams,
    presets: [
      { name: 'Gesang klar', params: { hpOn: 1, hpF: 90, hpSlope: 1, b1F: 320, b1G: -2.5, b1Q: 1.4, b2F: 3200, b2G: 1.5, b2Q: 0.9, hsF: 11000, hsG: 2.5 } },
      { name: 'Kick Druck', params: { hpOn: 1, hpF: 30, hpSlope: 1, lsF: 60, lsG: 2, b1F: 380, b1G: -4, b1Q: 1.6, b3F: 3800, b3G: 3, b3Q: 1.2 } },
      { name: 'Bass sauber', params: { hpOn: 1, hpF: 35, hpSlope: 2, b1F: 220, b1G: -2.5, b1Q: 1.2, b2F: 900, b2G: 1.5, b2Q: 1 } },
      { name: 'Akustikgitarre', params: { hpOn: 1, hpF: 110, hpSlope: 1, b1F: 200, b1G: -3, b1Q: 1, b3F: 5000, b3G: 1.5, b3Q: 0.8, hsG: 2 } },
      { name: 'Platz für die Stimme', params: { b2F: 2500, b2G: -2.5, b2Q: 1.2 } },
      { name: 'Mix-Bus Glanz', params: { lsF: 45, lsG: 0.8, b1F: 300, b1G: -0.8, b1Q: 0.8, hsF: 12000, hsG: 1.2 } },
    ],
    create: (ctx, p) => createEq(ctx, p),
  },
  {
    id: 'srs:comp', name: 'SRS Kompressor', short: 'Komp', role: 'comp', group: 'Dynamik',
    params: [
      { key: 'thr', label: 'Schwelle', min: -60, max: 0, def: -18, unit: 'dB', step: 0.1 },
      { key: 'ratio', label: 'Ratio', min: 1, max: 20, def: 4, unit: ':1', scale: 'log', step: 0.1 },
      { key: 'atk', label: 'Attack', min: 0.1, max: 100, def: 10, unit: 'ms', scale: 'log', step: 0.1 },
      { key: 'rel', label: 'Release', min: 10, max: 2000, def: 120, unit: 'ms', scale: 'log' },
      { key: 'knee', label: 'Knie', min: 0, max: 24, def: 6, unit: 'dB', step: 0.5 },
      { key: 'makeup', label: 'Makeup', min: -12, max: 24, def: 0, unit: 'dB', step: 0.1 },
      { key: 'auto', label: 'Auto-Makeup', min: 0, max: 1, def: 1, bool: true },
      { key: 'mix', label: 'Mix', min: 0, max: 100, def: 100, unit: '%' },
      { key: 'schp', label: 'Sidechain-HP', min: 20, max: 500, def: 20, unit: 'Hz', scale: 'log' },
      { key: 'det', label: 'Detektor', min: 0, max: 1, def: 0, choices: ['Peak', 'RMS'] },
      { key: 'char', label: 'Charakter', min: 0, max: 3, def: 0, choices: CHARACTERS },
    ],
    presets: [
      { name: 'Gesang glatt', params: { thr: -24, ratio: 3, atk: 8, rel: 90, knee: 8, char: 1, schp: 100 } },
      { name: 'Gesang nach vorn', params: { thr: -20, ratio: 5, atk: 3, rel: 60, knee: 4, char: 2, schp: 120 } },
      { name: 'Drums Punch', params: { thr: -16, ratio: 4, atk: 18, rel: 80, knee: 3, char: 2 } },
      { name: 'Bass fest', params: { thr: -22, ratio: 4, atk: 12, rel: 140, knee: 6, char: 1 } },
      { name: 'Bus Glue', params: { thr: -14, ratio: 2, atk: 30, rel: 300, knee: 6, char: 3, schp: 90 } },
      { name: 'Parallel Smash', params: { thr: -35, ratio: 10, atk: 1, rel: 60, knee: 0, mix: 35, char: 2 } },
    ],
    create: (ctx, p) => worklet(ctx, 'srs-comp', p),
  },
  {
    id: 'srs:deesser', name: 'SRS De-Esser', short: 'De-Ess', role: 'deesser', group: 'Dynamik',
    params: [
      { key: 'freq', label: 'Frequenz', min: 2000, max: 12000, def: 6500, unit: 'Hz', scale: 'log' },
      { key: 'thr', label: 'Schwelle', min: -60, max: 0, def: -30, unit: 'dB', step: 0.1 },
      { key: 'range', label: 'Bereich', min: 0, max: 24, def: 6, unit: 'dB', step: 0.1 },
      { key: 'listen', label: 'Abhören', min: 0, max: 1, def: 0, bool: true },
    ],
    presets: [{ name: 'Frauenstimme', params: { freq: 7500, thr: -32, range: 6 } }, { name: 'Männerstimme', params: { freq: 5500, thr: -30, range: 6 } }],
    create: (ctx, p) => worklet(ctx, 'srs-deess', p),
  },
  {
    id: 'srs:gate', name: 'SRS Gate', short: 'Gate', role: 'gate', group: 'Dynamik',
    params: [
      { key: 'thr', label: 'Schwelle', min: -80, max: 0, def: -45, unit: 'dB', step: 0.1 },
      { key: 'range', label: 'Bereich', min: -80, max: 0, def: -40, unit: 'dB' },
      { key: 'atk', label: 'Attack', min: 0.05, max: 50, def: 0.5, unit: 'ms', scale: 'log', step: 0.01 },
      { key: 'hold', label: 'Hold', min: 0, max: 500, def: 30, unit: 'ms' },
      { key: 'rel', label: 'Release', min: 5, max: 2000, def: 120, unit: 'ms', scale: 'log' },
      { key: 'schp', label: 'Sidechain-HP', min: 20, max: 2000, def: 20, unit: 'Hz', scale: 'log' },
    ],
    presets: [{ name: 'Tom', params: { thr: -35, range: -30, hold: 60, rel: 200 } }, { name: 'Snare', params: { thr: -28, range: -24, hold: 20, rel: 90, schp: 150 } }],
    create: (ctx, p) => worklet(ctx, 'srs-gate', p),
  },
  {
    id: 'srs:saturation', name: 'SRS Sättigung', short: 'Sättigung', role: 'saturation', group: 'Farbe',
    params: [
      { key: 'type', label: 'Art', min: 0, max: 2, def: 0, choices: SAT_TYPES },
      { key: 'drive', label: 'Drive', min: 0, max: 24, def: 6, unit: 'dB', step: 0.1 },
      { key: 'tone', label: 'Klang', min: -6, max: 6, def: 0, unit: 'dB', step: 0.1 },
      { key: 'out', label: 'Ausgang', min: -18, max: 6, def: 0, unit: 'dB', step: 0.1 },
    ],
    presets: [{ name: 'Bass fürs Handy', params: { type: 0, drive: 9, tone: 1 } }, { name: 'Gesang warm', params: { type: 1, drive: 5, tone: -0.5 } }, { name: 'Drums Biss', params: { type: 2, drive: 8, tone: 1.5, out: -1 } }],
    create: (ctx, p) => createSat(ctx, p),
  },
  {
    id: 'srs:limiter', name: 'SRS Limiter', short: 'Limiter', role: 'limiter', group: 'Dynamik',
    params: [
      { key: 'gain', label: 'Gain', min: 0, max: 24, def: 0, unit: 'dB', step: 0.1 },
      { key: 'ceil', label: 'Ceiling', min: -12, max: 0, def: -1, unit: 'dB', step: 0.1 },
      { key: 'rel', label: 'Release', min: 1, max: 1000, def: 60, unit: 'ms', scale: 'log' },
    ],
    presets: [{ name: 'Streaming −14 LUFS', params: { gain: 4, ceil: -1, rel: 80 } }, { name: 'Laut (Club)', params: { gain: 9, ceil: -0.3, rel: 40 } }],
    create: (ctx, p) => worklet(ctx, 'srs-limit', p),
  },
  {
    id: 'srs:reverb', name: 'SRS Hall', short: 'Hall', role: 'reverb', group: 'Raum',
    params: [
      { key: 'type', label: 'Art', min: 0, max: 3, def: 0, choices: REVERB_TYPES },
      { key: 'decay', label: 'Nachhall', min: 0.2, max: 8, def: 1.8, unit: 's', scale: 'log', step: 0.01 },
      { key: 'pre', label: 'Pre-Delay', min: 0, max: 200, def: 20, unit: 'ms' },
      { key: 'damp', label: 'Dämpfung', min: 0, max: 100, def: 50, unit: '%' },
      { key: 'width', label: 'Breite', min: 0, max: 100, def: 100, unit: '%' },
      { key: 'locut', label: 'Low-Cut', min: 20, max: 1000, def: 180, unit: 'Hz', scale: 'log' },
      { key: 'hicut', label: 'High-Cut', min: 1000, max: 20000, def: 9000, unit: 'Hz', scale: 'log' },
      { key: 'mix', label: 'Mix', min: 0, max: 100, def: 25, unit: '%' },
    ],
    presets: [{ name: 'Vocal Plate', params: { type: 0, decay: 1.6, pre: 30, damp: 45, locut: 250, hicut: 9000 } }, { name: 'Kleiner Raum', params: { type: 1, decay: 0.6, pre: 5, damp: 55 } }, { name: 'Große Halle', params: { type: 2, decay: 3.2, pre: 40, damp: 60, locut: 200 } }],
    create: (ctx, p, env) => createReverb(ctx, p, env),
  },
  {
    id: 'srs:delay', name: 'SRS Delay', short: 'Delay', role: 'delay', group: 'Raum',
    params: [
      { key: 'div', label: 'Zeit', min: 0, max: 6, def: 2, choices: DELAY_DIVS },
      { key: 'ms', label: 'Zeit frei', min: 1, max: 2000, def: 375, unit: 'ms', scale: 'log' },
      { key: 'fb', label: 'Feedback', min: 0, max: 95, def: 30, unit: '%' },
      { key: 'pp', label: 'Ping-Pong', min: 0, max: 1, def: 0, bool: true },
      { key: 'hp', label: 'Low-Cut', min: 20, max: 2000, def: 200, unit: 'Hz', scale: 'log' },
      { key: 'lp', label: 'High-Cut', min: 1000, max: 20000, def: 6000, unit: 'Hz', scale: 'log' },
      { key: 'mix', label: 'Mix', min: 0, max: 100, def: 25, unit: '%' },
    ],
    presets: [{ name: 'Slapback', params: { div: 6, ms: 95, fb: 8, mix: 20, lp: 5000 } }, { name: 'Ping-Pong 1/8', params: { div: 1, fb: 35, pp: 1, lp: 5500 } }, { name: 'Ballade 1/4', params: { div: 0, fb: 40, lp: 4500, hp: 300 } }],
    create: (ctx, p, env) => createDelay(ctx, p, env),
  },
  {
    id: 'srs:utility', name: 'SRS Utility', short: 'Utility', role: 'gain', group: 'Werkzeug',
    params: [
      { key: 'gain', label: 'Gain', min: -24, max: 24, def: 0, unit: 'dB', step: 0.1 },
      { key: 'width', label: 'Breite', min: 0, max: 200, def: 100, unit: '%' },
      { key: 'phaseL', label: 'Phase L', min: 0, max: 1, def: 0, bool: true },
      { key: 'phaseR', label: 'Phase R', min: 0, max: 1, def: 0, bool: true },
      { key: 'mono', label: 'Mono', min: 0, max: 1, def: 0, bool: true },
    ],
    presets: [],
    create: (ctx, p) => createUtility(ctx, p),
  },
];

/** Eigene Plugins über die Bridge: im Browser durchgereicht, klingen auf dem Studio-Rechner */
export const bridgeDef = (id: string, name = 'Plugin'): PluginDef => ({
  id, name, short: name.slice(0, 8), role: 'bridge', group: 'Bridge', params: [], presets: [],
  create: (ctx) => { const g = stereoGain(ctx); return { input: g, output: g, set() {}, dispose() { g.disconnect(); } }; },
});

const LEGACY: Record<string, string> = { 'srs:gain': 'srs:utility', 'srs:sat': 'srs:saturation' };
export function pluginDef(id: string): PluginDef | undefined {
  if (id.startsWith('bridge:')) return bridgeDef(id, id.slice(7));
  const real = LEGACY[id] ?? id;
  return PLUGINS.find((p) => p.id === real);
}
/** Plugin für eine Rolle (Auto-Mix) */
export const pluginForRole = (role: string) => PLUGINS.find((p) => p.role === role);

/** Gespeicherte Parameter mit Standardwerten auffüllen; ältere Parameternamen werden übernommen */
export function resolveParams(def: PluginDef, raw: Params = {}): Params {
  const out: Params = {};
  for (const d of def.params) out[d.key] = d.def;
  if (def.id === 'srs:eq') {
    if (raw.hp !== undefined && raw.hpF === undefined) { out.hpOn = raw.hp > 20.5 ? 1 : 0; out.hpF = raw.hp; out.hpSlope = 0; }
    if (raw.peqG !== undefined && raw.b2G === undefined) { out.b2F = raw.peqF ?? 1000; out.b2Q = raw.peqQ ?? 1; out.b2G = raw.peqG; }
    if (raw.shelfG !== undefined && raw.hsG === undefined) { out.hsF = raw.shelfF ?? 10000; out.hsG = raw.shelfG; }
  }
  if (def.id === 'srs:utility' && raw.db !== undefined && raw.gain === undefined) out.gain = raw.db;
  if (def.id === 'srs:comp' && raw.threshold !== undefined && raw.thr === undefined) { out.thr = raw.threshold; if (raw.ratio) out.ratio = raw.ratio; out.auto = 0; }
  for (const d of def.params) if (typeof raw[d.key] === 'number' && Number.isFinite(raw[d.key])) out[d.key] = Math.max(d.min, Math.min(d.max, raw[d.key]));
  return out;
}

/** Standard-Busse: Hall und Delay. Ältere Sends mit busId 'reverb' landen automatisch im Hall-Bus. */
export function defaultBusses(): Track[] {
  const bus = (id: string, name: string, color: string, plugin: string, params: Params): Track => ({
    id, name, color, kind: 'bus', armed: false, mute: false, solo: false, volumeDb: 0, pan: 0, sends: [],
    inserts: [{ id: `ins-${id}`, role: plugin.slice(4), plugin, params, bypass: false }],
  });
  return [
    bus('reverb', 'Hall', '#6b4fbb', 'srs:reverb', { type: 0, decay: 1.8, pre: 25, mix: 100 }),
    bus('delay', 'Delay', '#0f8b8d', 'srs:delay', { div: 2, fb: 28, mix: 100, lp: 5500, hp: 250 }),
  ];
}
export const bussesOf = (p: { busses?: Track[] }) => p.busses ?? defaultBusses();

/** Kurzbezeichnung eines Inserts für den Kanalzug */
export function slotLabel(s: InsertSlot): string {
  const d = pluginDef(s.plugin);
  return d ? (d.group === 'Bridge' ? d.name : d.short) : s.plugin;
}
